/**
 * กระเด็น / ล้ม / ลุก (the knock layer of the bout, lane DUEL-ARMOR; design docs/design/combat/2026-10-11-duel-armor.md §2).
 * A move may carry a knock tag; when it lands unguarded on a fighter with no armour it throws them:
 * - `fling` (กระเด็น): an arc back along the blow, `dist` metres over `KNOCK.air` s, then down;
 * - `down` (ล้ม): a trip, down at once after `KNOCK.trip` s.
 * Down: `KNOCK.down` s on the ground, then the get-up the fighter chose (rise, rise guarding, roll, rising attack, stay down).
 * A downed fighter CAN be hit (user, 2026-10-11) but only by low moves (sweeps, circles round the striker's feet, a dive),
 * at `KNOCK.otgMult` damage, once per knock-down, with no new reaction; other blows pass over the body.
 * Data only; the rules are systems/duelKnock.ts. Starting numbers, ns-balance tunes.
 */
import { longMoveOf } from './longMoves';

export type KnockKind = 'fling' | 'down';

export interface KnockTag {
  readonly kind: KnockKind;
  /** `fling`: metres thrown back. */
  readonly dist?: number;
}

export const KNOCK = {
  /** A fling's flight (s) and its apex (m, drawn only: the rules keep the body on the ground plane). */
  air: 0.55,
  apex: 1.0,
  /** A trip's fall (s) before the fighter is down. */
  trip: 0.3,
  /** On the ground before the get-up (s). */
  down: 0.8,
  /** Stay down: once a knock-down, this much longer (s). */
  stayDown: 0.5,
  /** The plain rise (s), and rise guarding (the guard is up on its last frame). */
  rise: 0.4,
  /** The roll: metres, seconds (i-frames all through), the stop after, its พลังกาย. */
  roll: { dist: 1.8, time: 0.35, stop: 0.12, cost: 20 },
  /** The rising attack: startup (the first `invuln` of it untouchable), reach, power, recovery when it found nothing or a guard. */
  riseAttack: { startup: 0.3, invuln: 0.2, reach: 2.0, power: 0.6, recovery: 0.5 },
  /** One blow on a flung body while it flies: at this share of its damage, no new reaction. */
  juggleMult: 0.7,
  /** One low blow on a downed body: at this share of its damage, no new reaction. */
  otgMult: 0.5,
  /** After getting up: no new fling or trip for this long (s); the blow lands as a shove with a short stun instead. */
  steady: 2.0,
  steadyStun: 0.3,
  /** Never flung or tripped twice inside this (s), counted from the first. */
  repeatGap: 3.0,
} as const;

/** Low moves that reach a downed body (besides any circle round the striker's feet and the dive strike). */
export const OTG_MOVES: readonly string[] = ['bs-sweep', 'ar-sweep', 'rc-reap'];

/** The knock tags (skill id -> tag). The ultimates throw; the sweeps trip. */
export const KNOCK_MOVES: Readonly<Record<string, KnockTag>> = {
  // ultimates: a fling
  'bs-tempest': { kind: 'fling', dist: 3.0 },
  'ar-buddha': { kind: 'fling', dist: 4.5 },
  'jz-scroll': { kind: 'fling', dist: 3.5 },
  'cp-heaven': { kind: 'fling', dist: 3.0 },
  'tf-supreme': { kind: 'fling', dist: 3.5 },
  'pp-tathagata': { kind: 'fling', dist: 3.5 },
  'fm-vanquish': { kind: 'fling', dist: 4.0 },
  'ah-imperial': { kind: 'fling', dist: 4.0 },
  'jy-dragon': { kind: 'fling', dist: 3.0 },
  'jl-abyss': { kind: 'fling', dist: 3.0 },
  'dd-heaven': { kind: 'fling', dist: 3.0 },
  'sg-masterpiece': { kind: 'fling', dist: 3.0 },
  'sp-decree': { kind: 'fling', dist: 3.0 },
  'dp-eighteen': { kind: 'fling', dist: 3.5 },
  'dp9-sky': { kind: 'fling', dist: 4.0 },
  'ns-heaven': { kind: 'fling', dist: 3.5 },
  // sweeps: a trip
  'bs-sweep': { kind: 'down' },
  'ar-sweep': { kind: 'down' },
  'rc-reap': { kind: 'down' },
};

export function knockTagOf(skillId: string): KnockTag | null {
  // A ท่าใหญ่ run as a long move (data/longMoves) throws by its own beats (the finisher's knock, then the fall beat).
  if (longMoveOf(skillId)?.kind === 'ult') return null;
  return KNOCK_MOVES[skillId] ?? null;
}
